'use client';

import { useEffect, useRef, useState } from 'react';
import { Play, Pause, CheckCircle2, X } from 'lucide-react';
import { useGuidedTour } from './GuidedTourProvider';
import { resolveFlowFormats } from './utils/formatResolver';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface GuidedTourVideoCardProps {
  onComplete?: () => void;
}

export function GuidedTourVideoCard({ onComplete }: GuidedTourVideoCardProps) {
  const { isOpen, engine, progress, selectedFlow } = useGuidedTour();
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasWatchedEnough, setHasWatchedEnough] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const resolution = selectedFlow ? resolveFlowFormats(selectedFlow) : null;
  const videoUrl = resolution?.videoUrl;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    const handleTimeUpdate = () => {
      if (video.duration && video.currentTime / video.duration >= 0.8) {
        setHasWatchedEnough(true);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setHasWatchedEnough(true);
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('ended', handleEnded);
    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('ended', handleEnded);
    };
  }, [videoUrl]);

  // Mark the first step as verified when the user has watched enough.
  useEffect(() => {
    if (hasWatchedEnough && selectedFlow?.steps[0]?.id) {
      progress.markStepVerified(selectedFlow.steps[0].id);
      onComplete?.();
    }
  }, [hasWatchedEnough, onComplete, progress, selectedFlow]);

  if (!isOpen || engine.viewMode !== 'runner' || engine.currentFormat !== 'video' || engine.isMinimized) {
    return null;
  }

  if (!videoUrl) {
    return (
      <div className="pointer-events-auto fixed bottom-4 right-4 z-[70] w-[min(92vw,24rem)]">
        <Card className="border-slate-200/80 bg-white/95 shadow-2xl backdrop-blur-md dark:border-slate-700/80 dark:bg-slate-800/95">
          <CardContent className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">
            No video available for this flow.
            <Button className="mt-4" size="sm" onClick={() => engine.setCurrentFormat('interactive')}>
              Try interactive tour
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    // isPlaying is driven by the video's own play/pause events so state can
    // never desync from what the native controls do.
    if (video.paused) {
      void video.play();
    } else {
      video.pause();
    }
  };

  return (
    <div className="pointer-events-auto fixed bottom-4 right-4 z-[70] w-[min(92vw,28rem)]">
      <Card className="border-slate-200/80 bg-white/95 shadow-2xl backdrop-blur-md dark:border-slate-700/80 dark:bg-slate-800/95">
        <CardHeader className="flex flex-row items-start justify-between gap-2 pb-2">
          <div>
            <CardTitle className="text-base">{selectedFlow?.label}</CardTitle>
            <p className="text-muted-foreground text-xs">
              {hasWatchedEnough ? (
                <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                  <CheckCircle2 className="h-3 w-3" />
                  Marked complete
                </span>
              ) : (
                'Watch the video to complete this flow.'
              )}
            </p>
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={engine.minimize} title="Minimize tour">
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-3 pt-0">
          <div className="relative aspect-video overflow-hidden rounded-lg bg-slate-900">
            <video
              ref={videoRef}
              src={videoUrl}
              className="h-full w-full"
              controls
              playsInline
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
            />
            {!isPlaying && (
              <button
                onClick={togglePlay}
                className="absolute inset-0 flex items-center justify-center bg-black/30 transition-colors hover:bg-black/40"
              >
                <Play className="h-12 w-12 text-white" />
              </button>
            )}
          </div>

          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={() => engine.setCurrentFormat('interactive')}
            >
              Switch to interactive
            </Button>
            <Button size="sm" disabled={!hasWatchedEnough} onClick={engine.goToNextStep}>
              {engine.isLastStep ? 'Finish' : 'Continue'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
